import React, { useState, useEffect } from "react";
import { Box, Typography, Button, Tabs, Tab, Alert, CircularProgress } from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../../store";
import { fetchSourcingOverview, fetchSourcingPerformance } from "../../../store/procurementDashboardSlice";
import OverviewDashboard from "./OverviewDashboard";
import AnalyticsDashboard from "./AnalyticsDashboard";
import PerformanceDashboard from "./PerformanceDashboard";
import ReportsDashboard from "./ReportsDashboard";
import overviewIcon from "../../../assets/images/overviewImg.svg";
import analyticsIcon from "../../../assets/images/analyticsIcon.svg";
import performanceIcon from "../../../assets/images/performanceImg.svg";
import supplierIcon from "../../../assets/images/supplierIcon1.svg";
import styles from "./sourcingSection.module.scss";

const SourcingSection = () => {
  const dispatch = useAppDispatch();
  const [dashboardTab, setDashboardTab] = useState(0);
  const {
    sourcingOverview,
    sourcingLoading,
    sourcingError,
    sourcingPerformance,
    sourcingPerformanceLoading,
    sourcingPerformanceError,
  } = useSelector((state: RootState) => state.procurementDashboard);

  useEffect(() => {
    dispatch(fetchSourcingOverview());
    dispatch(fetchSourcingPerformance());
  }, [dispatch]);

  const handleRefresh = () => {
    dispatch(fetchSourcingOverview());
    dispatch(fetchSourcingPerformance());
  };

  // Transform API data to component format
  const transformedOverviewData =
    sourcingOverview && sourcingPerformance
      ? {
          primaryCards: {
            totalPRs: {
              count: sourcingOverview.total_prs,
              label: "Total PRs",
            },
            totalPOs: {
              count: sourcingOverview.total_pos,
              label: "Total POs",
            },
            inProcessPRs: {
              count: sourcingOverview.in_process_prs,
              label: "In-Process PRs",
            },
            nonCompliancePRs: {
              count: sourcingOverview.non_compliance_prs,
              label: "Non-compliance PRs",
            },
          },
          secondaryCards: {
            successRate: sourcingOverview.close_pr_pct,
            defAvgDays: sourcingOverview.pct_avg_days,
            poValueTotal: sourcingOverview.po_value,
            singleSourcePRs: sourcingOverview.single_source_prs,
            quoteRespTotal: sourcingOverview.savings_usd,
            savingsPct: sourcingOverview.savings_pct_avg,
          },
          prByMonth: sourcingPerformance.pr_by_month.map((item) => ({
            month: item.month_name,
            closed: item.closed_prs,
            open: item.open_prs,
          })),
          prByStrategy: sourcingOverview.pr_by_strategy.map((item) => ({
            strategy: item.sourcing_strategy,
            value: item.count,
          })),
          delayedVsOpenVsClosed: [],
        }
      : null;

  const transformedAnalyticsData = sourcingOverview
    ? {
        awardBySuppliers: sourcingOverview.award_by_suppliers_usd.map(
          (item) => ({
            supplier: item.supplier_name,
            value: item.total_award_usd,
            poCount: item.total_pos,
          })
        ),
        awardByCategory: sourcingOverview.award_by_category_usd.map(
          (item) => ({
            category: item.g42_category,
            value: item.percentage_share,
          })
        ),
        awardByEntityUSD: sourcingOverview.award_by_entity_usd.map((item) => ({
          entity: item.location_code,
          value: item.total_award_usd,
        })),
        awardByEntityPOCount: sourcingOverview.award_by_entity_po_count.map(
          (item) => ({
            entity: item.location_code,
            count: item.total_po_count,
          })
        ),
        prByStatus: sourcingOverview.pr_by_status.map((item) => ({
          count: item.count,
          label: item.req_status
            .split("_")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" "),
          color:
            item.req_status === "partially_received"
              ? "#ffa94d"
              : item.req_status === "received"
              ? "#00d2aa"
              : "#5b6b9e",
        })),
      }
    : null;

  // Helper function to generate initials from buyer name
  const getInitials = (name: string): string => {
    const parts = name.split("_");
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };

  const transformedPerformanceData = sourcingPerformance
    ? {
        teamLeaderboard: sourcingPerformance.team_leaderboard.map(
          (item, index) => ({
            rank: index + 1,
            name: item.buyer_name.replace(/_/g, " "),
            initials: getInitials(item.buyer_name),
            poCount: item.total_prs,
            avgPct: Math.round(item.avg_pct_days * 10) / 10,
            savings: item.total_savings,
          })
        ),
        pctComparisonByBuyers:
          sourcingPerformance.pct_comparison_by_buyers.map((item) => ({
            buyer: item.buyer_name.replace(/_/g, " "),
            days: Math.round(item.avg_pct_days * 10) / 10,
          })),
        prByBuyers: sourcingPerformance.pr_by_buyers.map((item) => ({
          buyer: item.buyer_name.replace(/_/g, " "),
          completed: item.total_pr_count,
          total: item.total_pr_count,
        })),
      }
    : null;

  // Mock data for Reports Dashboard (no API endpoint available)
  const mockReportsData = {
    activePRs: [
      {
        prId: "PR-2026-1245",
        specialist: { name: "Sarah Johnson", initials: "SJ", avatarColor: "#00d2aa" },
        status: "In Review" as const,
        assignedDate: "2026-02-28",
        estDate: "2026-03-10",
        delay: { type: "on-track" as const },
      },
      {
        prId: "PR-2026-1244",
        specialist: { name: "Michael Chen", initials: "MC", avatarColor: "#5b6b9e" },
        status: "Approved" as const,
        assignedDate: "2026-02-25",
        estDate: "2026-03-08",
        delay: { type: "on-track" as const },
      },
      {
        prId: "PR-2026-1243",
        specialist: { name: "Emma Williams", initials: "EW", avatarColor: "#00d2aa" },
        status: "Delayed" as const,
        assignedDate: "2026-02-20",
        estDate: "2026-02-28",
        delay: { type: "delayed" as const, days: 4 },
      },
      {
        prId: "PR-2026-1242",
        specialist: { name: "David Miller", initials: "DM", avatarColor: "#5b6b9e" },
        status: "Pending" as const,
        assignedDate: "2026-02-26",
        estDate: "2026-03-12",
        delay: { type: "on-track" as const },
      },
      {
        prId: "PR-2026-1241",
        specialist: { name: "Jessica Brown", initials: "JB", avatarColor: "#00d2aa" },
        status: "In Review" as const,
        assignedDate: "2026-02-27",
        estDate: "2026-03-09",
        delay: { type: "on-track" as const },
      },
      {
        prId: "PR-2026-1240",
        specialist: { name: "Sarah Johnson", initials: "SJ", avatarColor: "#00d2aa" },
        status: "Delayed" as const,
        assignedDate: "2026-02-15",
        estDate: "2026-02-25",
        delay: { type: "delayed" as const, days: 7 },
      },
    ],
    topSavings: {
      value: "$1.38M",
      subtitle: "15.2% total savings rate",
    },
    bestPCT: {
      value: "12 Days",
      subtitle: "Sarah Johnson's average",
    },
    completionRate: {
      value: "77.4%",
      subtitle: "902 of 1166 PRs closed",
    },
  };

  const renderDashboardContent = () => {
    const isLoading = sourcingLoading || sourcingPerformanceLoading;
    const hasError = sourcingError || sourcingPerformanceError;

    if (isLoading) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "400px",
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    if (hasError) {
      return (
        <Alert severity="error" sx={{ m: 2 }}>
          {sourcingError || sourcingPerformanceError}
        </Alert>
      );
    }

    switch (dashboardTab) {
      case 0:
        return <OverviewDashboard {...(transformedOverviewData || {})} />;
      case 1:
        return <AnalyticsDashboard {...(transformedAnalyticsData || {})} />;
      case 2:
        return <PerformanceDashboard {...(transformedPerformanceData || {})} />;
      case 3:
        return <ReportsDashboard {...mockReportsData} />;
      default:
        return null;
    }
  };

  return (
    <Box className={styles.container}>
      <Box className={styles.header}>
        <Box className={styles.titleSection}>
          <Typography variant="h4" className={styles.title}>
            Sourcing Analytics
          </Typography>
          <Typography variant="body2" className={styles.subtitle}>
            Purchase requisitions and procurement performance
          </Typography>
        </Box>
        <Box className={styles.headerActions}>
          <Button
            variant="outlined"
            startIcon={<FilterListIcon />}
            className={styles.actionButton}
          >
            Filters
          </Button>
          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            className={styles.actionButton}
          >
            Export
          </Button>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            className={styles.refreshButton}
            onClick={handleRefresh}
            disabled={sourcingLoading || sourcingPerformanceLoading}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      <Box className={styles.dashboardTabs}>
        <Tabs
          value={dashboardTab}
          onChange={(_e, val) => setDashboardTab(val)}
          className={styles.tabs}
          TabIndicatorProps={{
            style: { backgroundColor: "#00d2aa" },
          }}
        >
          <Tab
            icon={ 
            <img
              src={overviewIcon}
              alt="Overview Dashboard"
              style={{
                width: 18,
                height: 18,
                marginRight: 6,
                filter:
                  dashboardTab === 0
                    ? "invert(60%) sepia(30%) saturate(1180%) hue-rotate(190deg) brightness(50%) contrast(90%)"
                    : "grayscale(100%) opacity(1)",
              }}
            />}
            label="Overview Dashboard"
            iconPosition="start"
            className={styles.tab}
            sx={{
              minHeight: "48px",
              textTransform: "none",
              color: "#666",
              "&.Mui-selected": {
                color: "#455391",
              },
            }}
          />
          <Tab
            icon={ 
            <img
              src={analyticsIcon}
              alt="Analytics Dashboard"
              style={{
                width: 18,
                height: 18,
                marginRight: 6,
                filter:
                  dashboardTab === 1
                    ? "invert(60%) sepia(30%) saturate(1180%) hue-rotate(190deg) brightness(50%) contrast(90%)"
                    : "grayscale(100%) opacity(1)",
              }}
            />}
            label="Analytics Dashboard"
            iconPosition="start"
            className={styles.tab}
            sx={{
              minHeight: "48px",
              textTransform: "none",
              color: "#666",
              "&.Mui-selected": {
                color: "#455391",
              },
            }}
          />
          <Tab
            icon={
              <img
              src={performanceIcon}
              alt="Performance Dashboard"
              style={{
                width: 18,
                height: 18,
                marginRight: 6,
                filter:
                  dashboardTab === 2
                    ? "invert(60%) sepia(30%) saturate(1180%) hue-rotate(190deg) brightness(50%) contrast(90%)"
                    : "grayscale(100%) opacity(1)",
              }}
            />
            }
            label="Performance Dashboard"
            iconPosition="start"
            className={styles.tab}
            sx={{
              minHeight: "48px",
              textTransform: "none",
              color: "#666",
              "&.Mui-selected": {
                color: "#455391",
              },
            }}
          />
          <Tab
            icon={
            <img
              src={supplierIcon}
              alt="Reports Dashboard"
              style={{
                width: 18,
                height: 18,
                marginRight: 6,
                filter:
                  dashboardTab === 3
                    ? "invert(60%) sepia(30%) saturate(1180%) hue-rotate(190deg) brightness(50%) contrast(90%)"
                    : "grayscale(100%) opacity(1)",
              }}
            />}
            label="Reports Dashboard"
            iconPosition="start"
            className={styles.tab}
            sx={{
              minHeight: "48px",
              textTransform: "none",
              color: "#666",
              "&.Mui-selected": {
                color: "#455391",
              },
            }}
          />
        </Tabs>
      </Box>

      {renderDashboardContent()}
    </Box>
  );
};

export default SourcingSection;
